import axios from "axios";
import { openDB } from "idb";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────
// Offline-first altyapı: IndexedDB tabanlı GET önbelleği + yazma kuyruğu
// Tek kullanıcı / tek tablet senaryosu için tasarlanmıştır (çakışma yok).
// ─────────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const dbp = openDB("mimgida-offline", 1, {
  upgrade(db) {
    if (!db.objectStoreNames.contains("getcache")) db.createObjectStore("getcache");
    if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "id" });
    if (!db.objectStoreNames.contains("files")) db.createObjectStore("files", { keyPath: "id" });
  },
});

// ── Event bus (UI abonelikleri) ──
const listeners = new Set();
export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((f) => { try { f(); } catch {} });

export const isOnline = () => navigator.onLine;
let processing = false;
let syncing = false;
export const getSyncing = () => syncing;

// ── GET önbelleği ──
export function cacheKey(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  const params = config.params ? JSON.stringify(config.params) : "";
  return `${method}:${url}?${params}`;
}
export async function putCache(config, data) {
  try { const db = await dbp; await db.put("getcache", { data, ts: Date.now() }, cacheKey(config)); } catch {}
}
export async function getCache(config) {
  try { const db = await dbp; const r = await db.get("getcache", cacheKey(config)); return r ? r.data : undefined; }
  catch { return undefined; }
}

// ── temp id → gerçek id eşlemesi (offline oluşturulan kayıtlara bağlı işlemler için) ──
let idMap = {};
(async () => { try { const db = await dbp; const m = await db.get("getcache", "__idmap"); if (m) idMap = m.data || {}; } catch {} })();
async function saveIdMap() { try { const db = await dbp; await db.put("getcache", { data: idMap, ts: Date.now() }, "__idmap"); } catch {} }
function remapStr(s) { if (typeof s !== "string") return s; let out = s; for (const [t, r] of Object.entries(idMap)) { if (out.includes(t)) out = out.split(t).join(r); } return out; }
function remapData(d) {
  if (d == null) return d;
  if (typeof d === "string") return remapStr(d);
  if (Array.isArray(d)) return d.map(remapData);
  if (typeof d === "object") { const o = {}; for (const k in d) o[k] = remapData(d[k]); return o; }
  return d;
}

// ── İnsan-okunur etiketler ──
const VERB = { post: "Kayıt", put: "Güncelleme", patch: "Güncelleme", delete: "Silme" };
const MAP = [
  [/^\/production/, "Günlük Üretim"],
  [/^\/feed-purchases/, "Yem Alımı"],
  [/^\/feed-consumption/, "Yem Tüketimi"],
  [/^\/feed/, "Yem & Stok"],
  [/^\/medicine-applications/, "İlaç / Aşı Uygulaması"],
  [/^\/medicines/, "İlaç Kaydı"],
  [/^\/shipments/, "Sevkiyat"],
  [/^\/transfers/, "Depolar Arası Transfer"],
  [/^\/customers/, "Müşteri"],
  [/^\/payments/, "Tahsilat / Ödeme"],
  [/^\/coops/, "Kümes"],
  [/^\/coop-media/, "Kümes Medya"],
  [/^\/alerts/, "Kümes Durum Bildirimi"],
  [/^\/depot\/count/, "Depo Sayım (Mutabakat)"],
  [/^\/depot\/intake/, "Depo Mal Girişi"],
  [/^\/depot/, "Ana Depo İşlemi"],
  [/^\/suppliers/, "Tedarikçi"],
  [/^\/settings/, "Ayarlar"],
  [/^\/users/, "Kullanıcı"],
  [/^\/auth\/change-password/, "Şifre Değişikliği"],
];
export function labelFor(method, url, data) {
  const v = VERB[(method || "").toLowerCase()] || "İşlem";
  let entity = "İşlem";
  for (const [re, name] of MAP) { if (re.test(url)) { entity = name; break; } }
  const date = data && typeof data === "object" && data.date ? ` · ${data.date}` : "";
  return `${entity} — ${v}${date}`;
}

// ── Kuyruk CRUD ──
function newId(p = "q") { return `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }

export async function enqueue(task) {
  const db = await dbp;
  const item = { id: newId(), ts: Date.now(), status: "pending", error: null, ...task };
  await db.put("queue", item);
  emit();
  if (isOnline()) processQueue();
  return item;
}

export async function enqueueMedia({ blob, filename, content_type, coop_id, date, note, coop_name }) {
  return enqueue({
    kind: "media", blob, filename, content_type, coop_id, date, note,
    label: `Kümes Medya — Yükleme${coop_name ? ` · ${coop_name}` : ""} · ${filename}`,
  });
}

export async function enqueueFile({ url, label }) {
  return enqueue({ kind: "file", url, label: label || "Belge / PDF" });
}

export async function getQueue() { const db = await dbp; const all = await db.getAll("queue"); return all.sort((a, b) => a.ts - b.ts); }
export async function pendingCount() { const db = await dbp; return (await db.count("queue")); }
export async function deleteQueueItem(id) { const db = await dbp; await db.delete("queue", id); emit(); }
export async function updateQueueItem(id, patch) {
  const db = await dbp; const it = await db.get("queue", id);
  if (it) { await db.put("queue", { ...it, ...patch, status: "pending", error: null }); emit(); }
}

// Çevrimdışı devreden tavuk: önbellekteki üretim kayıtları + kuyruktaki girişlerden zinciri yerelde kurar
export async function offlineCarryover(coopId, date, baseCount = 0) {
  const db = await dbp;
  const byId = {};
  const keys = await db.getAllKeys("getcache");
  let known = null;
  const parse = (d) => { if (typeof d !== "string") return d; try { return JSON.parse(d); } catch { return null; } };
  for (const k of keys) {
    if (typeof k !== "string" || !k.startsWith("get:/production")) continue;
    if (k.includes("carryover")) {
      const m = k.match(/coop_id=([^&?]+)&date=([\d-]+)/);
      if (m && m[1] === coopId && m[2] <= date && (!known || m[2] > known.date)) {
        const r = await db.get("getcache", k);
        const d = parse(r?.data);
        if (d) known = { date: m[2], opening: Number(d.opening_chickens || 0) };
      }
      continue;
    }
    const r = await db.get("getcache", k);
    const d = parse(r?.data);
    if (Array.isArray(d)) d.forEach((p) => { if (p?.id) byId[p.id] = p; });
  }
  for (const q of await getQueue()) {
    if (q.kind !== "mutation" || !/^\/production(\/|$|\?)/.test(q.url || "")) continue;
    const m = q.method.toLowerCase(); const pid = (q.url.split("/")[2] || "").split("?")[0];
    if (m === "post") byId[q.tempId] = { ...q.data, id: q.tempId };
    else if (m === "put" && pid) byId[pid] = { ...(byId[pid] || {}), ...q.data, id: pid };
    else if (m === "delete" && pid) delete byId[pid];
  }
  const prev = Object.values(byId)
    .filter((p) => p.coop_id === coopId && p.date && p.date < date)
    .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  if (prev && (!known || prev.date >= known.date)) return Number(prev.closing_chickens || 0);
  if (known) return known.opening;
  return Number(baseCount || 0);
}

// Çevrimdışı açılışa hazır mı? (SW aktif + uygulama kabuğu + kümes listesi önbellekte)
export async function offlineReady() {
  try {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller || !window.caches) return false;
    const shell = await caches.match("/index.html");
    const db = await dbp;
    const coops = await db.get("getcache", "get:/coops?");
    return !!shell && !!coops;
  } catch { return false; }
}

// ── Hazır (indirilmiş) belgeler ──
export async function getReadyFiles() { const db = await dbp; const all = await db.getAll("files"); return all.sort((a, b) => b.ts - a.ts); }
export async function readyFilesCount() { const db = await dbp; return (await db.count("files")); }
export async function openReadyFile(id) {
  const db = await dbp; const f = await db.get("files", id);
  if (!f) return;
  const url = URL.createObjectURL(f.blob);
  const isPdf = (f.content_type || "").includes("pdf") || (f.blob.type || "").includes("pdf");
  if (isPdf) {
    window.open(url, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = url; a.download = f.name || "belge";
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export async function deleteReadyFile(id) { const db = await dbp; await db.delete("files", id); emit(); }

// Oturum kapanınca önbellek/hazır belgeleri temizle (ortak tablet gizliliği) — bekleyen kuyruk korunur
export async function clearOfflineCache() {
  try { const db = await dbp; await db.clear("getcache"); await db.clear("files"); idMap = {}; emit(); } catch {}
}

// ── Replay için saf ağ instance'ı (offline adapter YOK) ──
const net = axios.create({ baseURL: API_BASE });
net.interceptors.request.use((cfg) => { const t = localStorage.getItem("token"); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });

// ── Senkronizasyon motoru ──
export async function processQueue() {
  if (processing || !isOnline()) return;
  processing = true; syncing = true; emit();
  try {
    const items = await getQueue();
    for (const it of items) {
      try {
        if (it.kind === "media") {
          await replayMedia(it);
        } else if (it.kind === "file") {
          await replayFile(it);
        } else {
          await replayMutation(it);
        }
        await deleteQueueItem(it.id);
      } catch (err) {
        // Gerçek HTTP hatası → işareti koy ve dur (sıra/bağımlılık korunur)
        if (err && err.response) {
          const d = err.response.data?.detail;
          const msg = typeof d === "string" ? d : (err.message || "Sunucu hatası");
          await updateErrored(it.id, msg);
          break;
        }
        // Ağ koptu → senkronu bırak, online olunca tekrar denenecek
        break;
      }
    }
  } finally {
    processing = false; syncing = false; emit();
  }
}

async function updateErrored(id, message) {
  const db = await dbp; const it = await db.get("queue", id);
  if (it) {
    await db.put("queue", { ...it, status: "error", error: message });
    emit();
    toast.error(`Bir işlem senkronize edilemedi: ${message}. Senkronizasyon panelinden düzenleyin veya silin.`, { duration: 6000 });
  }
}

async function replayMutation(it) {
  const url = remapStr(it.url);
  const data = remapData(it.data);
  const res = await net.request({ method: it.method, url, data });
  if (it.tempId && res?.data && res.data.id) { idMap[it.tempId] = res.data.id; await saveIdMap(); }
  return res;
}

const CHUNK = 5 * 1024 * 1024;
async function replayMedia(it) {
  const upload_id = newId("up");
  const total = Math.ceil(it.blob.size / CHUNK) || 1;
  for (let i = 0; i < total; i++) {
    const part = it.blob.slice(i * CHUNK, (i + 1) * CHUNK);
    const fd = new FormData();
    fd.append("upload_id", upload_id);
    fd.append("index", String(i));
    fd.append("file", part, it.filename);
    await net.post("/coop-media/chunk", fd);
  }
  await net.post("/coop-media/complete", {
    upload_id, coop_id: remapStr(it.coop_id), date: it.date, note: it.note || "",
    filename: it.filename, content_type: it.content_type || "application/octet-stream",
  });
}

async function replayFile(it) {
  const url = remapStr(it.url);
  const res = await net.get(url, { responseType: "blob", baseURL: undefined });
  const db = await dbp;
  const ct = res.headers?.["content-type"] || res.data?.type || "";
  const ext = ct.includes("pdf") ? ".pdf" : ct.includes("sheet") || ct.includes("excel") ? ".xlsx" : ct.includes("csv") ? ".csv" : "";
  const base = (it.label || "belge").replace(/[^\wğüşıöçĞÜŞİÖÇ .-]/gi, "_").trim();
  await db.put("files", { id: newId("f"), ts: Date.now(), label: it.label, name: base + ext, content_type: ct, blob: res.data });
  emit();
}

// online olunca otomatik senkron
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { emit(); processQueue(); });
  window.addEventListener("offline", () => emit());
  // ilk yüklemede bekleyen varsa dene
  setTimeout(() => { if (isOnline()) processQueue(); }, 1500);
}
